// Forced-update gate.
//
// Native builds compile in the app version at build time and cannot self-update
// their Supabase URL — this is the mechanism that lets us eventually retire the
// last builds still calling <ref>.supabase.co directly (decision D-2026-09-15-T
// in mac-mini-supabase). It reads a small static JSON manifest served from the
// app's own web origin (not Supabase, so it keeps working through the cutover)
// and compares it against the installed native version.
//
// Fails open by design: any network error, timeout, 404 or malformed JSON must
// never block the app. A broken manifest is a silent no-op, not an outage.

export interface AppVersionManifest {
  min_version: string;
  latest_version: string;
  store_url_android: string;
  store_url_ios: string;
  message?: string;
}

export type UpdateGateStatus =
  | { kind: 'none' }
  | { kind: 'recommended'; manifest: AppVersionManifest }
  | { kind: 'required'; manifest: AppVersionManifest };

// Founder-confirmed production web origin (15 Sep 2026): https://council.ceo.
// Manifest is served from Council's own domain, not Supabase, so it keeps
// working through the cutover.
const MANIFEST_URL = 'https://council.ceo/app-version.json';
const FETCH_TIMEOUT_MS = 4000;
const MIN_RECHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h

/**
 * Numeric semver-ish comparison: "1.10.0" > "1.9.0". Non-numeric or missing
 * segments compare as 0, so "1.2" and "1.2.0" are equal, and garbage input
 * never throws — it just compares as if it were "0".
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
}

export function isValidManifest(value: unknown): value is AppVersionManifest {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.min_version === 'string' &&
    typeof v.latest_version === 'string' &&
    typeof v.store_url_android === 'string' &&
    typeof v.store_url_ios === 'string'
  );
}

/**
 * Fetches the manifest with a hard timeout. Returns null on any failure — never
 * throws. `timeoutMs` defaults to the real 4s budget; tests pass a small value
 * so a timeout case doesn't have to wait 4 real seconds.
 */
export async function fetchAppVersionManifest(
  url: string = MANIFEST_URL,
  timeoutMs: number = FETCH_TIMEOUT_MS,
): Promise<AppVersionManifest | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal, cache: 'no-store' });
    if (!res.ok) return null;
    const json: unknown = await res.json();
    return isValidManifest(json) ? json : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export function evaluateUpdateGate(installedVersion: string, manifest: AppVersionManifest): UpdateGateStatus {
  if (compareVersions(installedVersion, manifest.min_version) < 0) {
    return { kind: 'required', manifest };
  }
  if (compareVersions(installedVersion, manifest.latest_version) < 0) {
    return { kind: 'recommended', manifest };
  }
  return { kind: 'none' };
}

let lastCheckAt: number | null = null;

/** True the first time, then true at most once per MIN_RECHECK_INTERVAL_MS. */
export function shouldCheckNow(now: number = Date.now()): boolean {
  if (lastCheckAt !== null && now - lastCheckAt < MIN_RECHECK_INTERVAL_MS) return false;
  lastCheckAt = now;
  return true;
}

/** Test-only: resets the internal recheck throttle. */
export function __resetUpdateGateThrottle(): void {
  lastCheckAt = null;
}

export const UPDATE_GATE_MANIFEST_URL = MANIFEST_URL;
